<template>
  <div class="flex min-h-0 flex-1 flex-col gap-2">
    <div class="flex flex-wrap items-center gap-1">
      <app-button
        v-for="option in tools"
        :key="option.value"
        size="xs"
        :icon="option.icon"
        :variant="tool === option.value ? '' : 'outline'"
        :tone="tool === option.value ? 'primary' : 'muted'"
        :label="option.label"
        :title="option.title"
        :data-test="`feedback-tool-${option.value}`"
        @click="selectTool(option.value)"
      />
      <span class="mx-1 h-5 w-px bg-theme-border" aria-hidden="true"></span>
      <app-button
        size="xs"
        variant="ghost"
        tone="muted"
        :icon="mdiPlusBoxOutline"
        :label="t('feedback.annotate.add_box')"
        :title="`${t('feedback.annotate.add_box')} (B)`"
        @click="addCentredShape"
      />
      <app-button
        v-if="shot?.crop"
        size="xs"
        variant="ghost"
        tone="muted"
        :icon="mdiCropFree"
        :label="t('feedback.annotate.reset_crop')"
        @click="resetCrop"
      />

      <!-- History lives on the right, away from the tools: undo is not a
           drawing action and reaching for it should not risk picking one. -->
      <div class="ml-auto flex items-center gap-1">
        <span class="mr-1 hidden text-xs text-theme-text-muted sm:inline">{{ phaseHint }}</span>
        <app-button
          size="xs"
          variant="outline"
          tone="muted"
          :icon="mdiUndo"
          :label="t('feedback.annotate.undo')"
          :title="`${t('feedback.annotate.undo')} (${undoKeyLabel})`"
          :disabled="!canUndo"
          data-test="feedback-undo"
          @click="undo"
        />
        <app-button
          size="xs"
          variant="outline"
          tone="muted"
          :icon="mdiRedo"
          :label="t('feedback.annotate.redo')"
          :title="`${t('feedback.annotate.redo')} (${redoKeyLabel})`"
          :disabled="!canRedo"
          data-test="feedback-redo"
          @click="redo"
        />
      </div>
    </div>

    <div class="flex min-h-0 flex-1 items-center justify-center">
      <!-- The stage hugs the canvas (inline-block, no padding) so the preview
           and the text overlay, both measured against the canvas, line up. -->
      <div
        ref="stage"
        :data-feedback-placing="phase > 0 ? 'true' : undefined"
        class="relative inline-block max-w-full cursor-crosshair select-none overflow-hidden rounded-theme-sm border border-theme-border bg-theme-bg-subtle"
        tabindex="0"
        role="application"
        :aria-label="t('feedback.annotate.canvas_label')"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      >
        <canvas ref="canvasEl" class="block max-h-[72vh] w-auto max-w-full" />

        <!-- Live preview of the shape being placed. A DOM overlay, not a canvas
             redraw: repainting a 1600px bitmap on every mousemove is wasteful
             and visibly laggy. -->
        <div
          v-if="previewBox"
          class="pointer-events-none absolute"
          :class="previewClass"
          :style="previewBox"
        ></div>
        <svg
          v-if="previewLine"
          class="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          aria-hidden="true"
        >
          <line
            :x1="previewLine.x1"
            :y1="previewLine.y1"
            :x2="previewLine.x2"
            :y2="previewLine.y2"
            class="stroke-theme-danger"
            :stroke-width="previewLine.width"
            stroke-linecap="round"
            marker-end="url(#feedback-arrowhead)"
          />
          <defs>
            <marker
              id="feedback-arrowhead"
              markerWidth="4"
              markerHeight="4"
              refX="3"
              refY="2"
              orient="auto"
            >
              <path d="M0,0 L4,2 L0,4 z" class="fill-theme-danger" />
            </marker>
          </defs>
        </svg>

        <!-- A real textarea over the box: caret, selection, IME and screen
             readers all come for free, and the canvas only has to render the
             committed string. -->
        <textarea
          v-if="editingStyle"
          ref="textInput"
          v-model="editingText"
          data-test="feedback-text-input"
          class="absolute resize-none rounded-theme-sm border-2 border-theme-danger bg-theme-surface leading-[1.35] text-theme-text outline-none"
          :style="editingStyle"
          :aria-label="t('feedback.annotate.text_label')"
          :placeholder="t('feedback.annotate.text_placeholder')"
          @keydown.stop="onTextKeyDown"
          @blur="commitText"
        ></textarea>
      </div>
    </div>
  </div>
</template>

<script setup>
  /**
   * Screenshot annotation: redact, highlight, arrow, caption, crop.
   *
   * Shapes are stored in SOURCE pixel coordinates on the draft, never baked
   * into the stored canvas — the same list is replayed at upload time by
   * `renderAnnotated`, which is what makes the redaction real rather than an
   * overlay. Nothing here mutates `shot.canvas`.
   *
   * Two ways to draw, because a screenshot is often bigger than the screen and
   * a press-drag-release across a scaled-down image is fiddly:
   *   - drag: press, move, release — the familiar way
   *   - click-click-click: the first click spawns a shape that follows the
   *     cursor, the second pins its top-left (tail, for an arrow), the third
   *     pins its bottom-right (head). Escape abandons it.
   *
   * Drawing a highlight drops straight into a caption next to it: marking a
   * thing and saying what is wrong with it are one thought, and making the
   * user pick a second tool in between is how screenshots end up with rings
   * around them and no explanation.
   *
   * Sizes are all ratios of the capture, never absolute pixels — an 18px
   * caption is fine on a 900px screenshot and invisible on a retina one.
   */
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    mdiArrowTopRight,
    mdiCrop,
    mdiCropFree,
    mdiFormatText,
    mdiPlusBoxOutline,
    mdiRectangle,
    mdiRectangleOutline,
    mdiRedo,
    mdiUndo,
  } from '@mdi/js';

  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import { useFeedbackReporter } from '@/features/feedback/composables/useFeedbackReporter';
  import {
    placeTextBox,
    placeTextBoxForArrow,
    renderAnnotated,
    strokeWidthFor,
    textBoxHeight,
    textFontSizeFor,
    textPadding,
  } from '@/features/feedback/domain/screenshot';

  const props = defineProps({
    shotId: { type: String, required: true },
  });

  const { t } = useI18n();
  // The shot is read from the draft rather than passed in: annotations mutate
  // it, and mutating a prop object is exactly the aliasing bug the lint rule
  // is there to stop. The draft is a module-level singleton, so this is the
  // same object either way — just owned by the composable that created it.
  const { screenshotById } = useFeedbackReporter();
  const shot = computed(() => screenshotById(props.shotId));

  // Rendered at roughly display resolution so a full-screen edit is sharp;
  // the upload is bounded separately by renderAnnotated's own cap.
  const PREVIEW_MAX_EDGE = 1600;
  const NUDGE_RATIO = 0.008; // of the region width, per arrow-key press
  const DRAG_THRESHOLD_PX = 4;
  const TOOL_KEYS = { r: 'redact', h: 'highlight', a: 'arrow', t: 'text', c: 'crop' };

  const canvasEl = ref(null);
  const stage = ref(null);
  const textInput = ref(null);
  const tool = ref('redact');
  const selectedIndex = ref(-1);
  const editingIndex = ref(-1);
  const editingText = ref('');
  const editingStyle = ref(null);

  /** The shape being placed, in SOURCE coordinates, or null. */
  const preview = ref(null);
  /** 0 = nothing in flight, 1 = following the cursor, 2 = sizing. */
  const phase = ref(0);
  let dragStart = null;
  let dragMoved = false;
  /** Tool to return to once an auto-spawned caption is finished. */
  let toolBeforeCaption = null;

  const isMac =
    typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform || '');
  const undoKeyLabel = isMac ? '⌘Z' : 'Ctrl+Z';
  const redoKeyLabel = isMac ? '⌘R' : 'Ctrl+R';

  /**
   * Undo/redo over whole-annotation snapshots.
   *
   * Snapshots rather than inverse operations: the state is a short list of
   * plain rectangles plus a crop, so copying it is cheap, and a crop changes
   * how every other shape is interpreted — which is exactly the case an
   * operation log gets wrong. The bitmap is never copied; it does not change.
   */
  const past = ref([]);
  const future = ref([]);
  const HISTORY_LIMIT = 50;

  const canUndo = computed(() => past.value.length > 0);
  const canRedo = computed(() => future.value.length > 0);

  function snapshot() {
    return {
      shapes: (shot.value?.shapes || []).map((shape) => ({ ...shape })),
      crop: shot.value?.crop ? { ...shot.value.crop } : null,
    };
  }

  function remember() {
    past.value.push(snapshot());
    if (past.value.length > HISTORY_LIMIT) past.value.shift();
    future.value = [];
  }

  function restore(state) {
    if (!shot.value) return;
    shot.value.shapes.splice(0, shot.value.shapes.length, ...state.shapes);
    shot.value.crop = state.crop;
    selectedIndex.value = shot.value.shapes.length - 1;
    redraw();
  }

  const tools = computed(() =>
    [
      { value: 'redact', icon: mdiRectangle, key: 'R', label: t('feedback.annotate.redact') },
      {
        value: 'highlight',
        icon: mdiRectangleOutline,
        key: 'H',
        label: t('feedback.annotate.highlight'),
      },
      { value: 'arrow', icon: mdiArrowTopRight, key: 'A', label: t('feedback.annotate.arrow') },
      { value: 'text', icon: mdiFormatText, key: 'T', label: t('feedback.annotate.text') },
      { value: 'crop', icon: mdiCrop, key: 'C', label: t('feedback.annotate.crop') },
    ].map((option) => ({ ...option, title: `${option.label} (${option.key})` }))
  );

  const phaseHint = computed(() => {
    if (phase.value === 1) return t('feedback.annotate.phase_position');
    if (phase.value === 2) return t('feedback.annotate.phase_size');
    return t('feedback.annotate.phase_idle');
  });

  // ── geometry ─────────────────────────────────────────────────────────

  function region() {
    const current = shot.value;
    if (!current) return { x: 0, y: 0, width: 1, height: 1 };
    return (
      current.crop || { x: 0, y: 0, width: current.canvas.width, height: current.canvas.height }
    );
  }

  const fontSize = computed(() => textFontSizeFor(shot.value?.canvas));

  function displayScale() {
    const bounds = canvasEl.value?.getBoundingClientRect();
    if (!bounds || !bounds.width) return 1;
    return bounds.width / region().width;
  }

  function toDisplay(shape) {
    const area = region();
    const scale = displayScale();
    return {
      left: (Math.min(shape.x, shape.x + shape.width) - area.x) * scale,
      top: (Math.min(shape.y, shape.y + shape.height) - area.y) * scale,
      width: Math.abs(shape.width) * scale,
      height: Math.abs(shape.height) * scale,
    };
  }

  const previewBox = computed(() => {
    const shape = preview.value;
    if (!shape || shape.kind === 'arrow') return null;
    const box = toDisplay(shape);
    return {
      left: `${box.left}px`,
      top: `${box.top}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
    };
  });

  const previewClass = computed(() => {
    const kind = preview.value?.kind;
    if (kind === 'redact') return 'bg-theme-text opacity-80';
    if (kind === 'text') return 'border-2 border-dashed border-theme-danger';
    return 'border-2 border-theme-danger';
  });

  const previewLine = computed(() => {
    const shape = preview.value;
    if (!shape || shape.kind !== 'arrow') return null;
    const area = region();
    const scale = displayScale();
    return {
      x1: (shape.x - area.x) * scale,
      y1: (shape.y - area.y) * scale,
      x2: (shape.x + shape.width - area.x) * scale,
      y2: (shape.y + shape.height - area.y) * scale,
      width: strokeWidthFor((canvasEl.value?.getBoundingClientRect().height || 0) || 600),
    };
  });

  /** Displayed pixels → source pixels. */
  function toSource(event) {
    const bounds = canvasEl.value.getBoundingClientRect();
    const area = region();
    return {
      x: area.x + (event.clientX - bounds.left) * (area.width / bounds.width),
      y: area.y + (event.clientY - bounds.top) * (area.height / bounds.height),
      clientX: event.clientX,
      clientY: event.clientY,
    };
  }

  // ── rendering ────────────────────────────────────────────────────────

  function redraw() {
    const target = canvasEl.value;
    if (!target || !shot.value) return;
    const rendered = renderAnnotated(
      shot.value.canvas,
      // The caption being typed is drawn by the textarea on top, so leave it
      // out of the bitmap — otherwise the two render one behind the other.
      shot.value.shapes.filter((_, index) => index !== editingIndex.value),
      shot.value.crop,
      PREVIEW_MAX_EDGE
    );
    target.width = rendered.width;
    target.height = rendered.height;
    target.getContext('2d').drawImage(rendered, 0, 0);
    if (editingIndex.value >= 0) syncEditingStyle();
  }

  function syncEditingStyle() {
    const shape = shot.value?.shapes[editingIndex.value];
    if (!shape) {
      editingStyle.value = null;
      return;
    }
    const box = toDisplay(shape);
    const scale = displayScale();
    const shapeFont = shape.fontSize || fontSize.value;
    editingStyle.value = {
      left: `${box.left}px`,
      top: `${box.top}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
      fontSize: `${shapeFont * scale}px`,
      padding: `${textPadding(shapeFont) * scale}px`,
    };
  }

  function selectTool(value) {
    cancelPlacement();
    // An explicit pick overrides any pending hand-back.
    toolBeforeCaption = null;
    tool.value = value;
    stage.value?.focus();
  }

  // ── text editing ─────────────────────────────────────────────────────

  let textBeforeEdit = '';

  async function startEditing(index) {
    editingIndex.value = index;
    textBeforeEdit = shot.value.shapes[index].text || '';
    editingText.value = textBeforeEdit;
    selectedIndex.value = index;
    redraw();
    await nextTick();
    textInput.value?.focus();
  }

  /** Escape and Cmd/Ctrl+Enter finish the caption; Enter alone adds a line. */
  function onTextKeyDown(event) {
    const finishes =
      event.key === 'Escape' || (event.key === 'Enter' && (event.metaKey || event.ctrlKey));
    if (!finishes) return;
    event.preventDefault();
    commitText();
  }

  function commitText() {
    const index = editingIndex.value;
    if (index < 0) return;
    const shape = shot.value?.shapes[index];
    editingIndex.value = -1;
    editingStyle.value = null;
    if (!shape) return;
    const text = editingText.value.trim();
    if (!text) {
      // An empty caption is just a box in the way.
      shot.value.shapes.splice(index, 1);
      selectedIndex.value = shot.value.shapes.length - 1;
    } else {
      // Only a RE-edit is its own history entry; first filling in a fresh
      // caption belongs to the gesture that created it.
      if (textBeforeEdit && text !== textBeforeEdit) remember();
      shape.text = text;
      shape.height = Math.max(shape.height, textBoxHeight(text, shape.width, shape.fontSize));
    }
    // An auto-spawned caption hands the tool back, whether it was filled in
    // or abandoned. An explicitly chosen Caption tool stays selected.
    if (toolBeforeCaption) {
      tool.value = toolBeforeCaption;
      toolBeforeCaption = null;
    }
    redraw();
    stage.value?.focus();
  }

  // Grow the box as the caption outgrows it, so nothing is typed out of sight.
  watch(editingText, (value) => {
    const shape = shot.value?.shapes[editingIndex.value];
    if (!shape) return;
    const needed = textBoxHeight(value, shape.width, shape.fontSize);
    if (needed > shape.height) {
      shape.height = needed;
      syncEditingStyle();
    }
  });

  // ── placement ────────────────────────────────────────────────────────

  /** Starting extent for a shape spawned by a click rather than a drag. */
  function defaultExtent() {
    const area = region();
    if (tool.value === 'arrow') return { width: area.width * 0.12, height: area.height * 0.08 };
    if (tool.value === 'text') {
      const width = area.width * 0.28;
      return { width, height: textBoxHeight('', width, fontSize.value) };
    }
    return { width: area.width * 0.18, height: area.height * 0.1 };
  }

  function cancelPlacement() {
    phase.value = 0;
    preview.value = null;
  }

  function advancePlacement(point) {
    if (phase.value === 0) {
      preview.value = { kind: tool.value, x: point.x, y: point.y, ...defaultExtent() };
      phase.value = 1;
      return;
    }
    if (phase.value === 1) {
      // Pin the corner it has been dragging around; the far corner takes over.
      preview.value = { ...preview.value, x: point.x, y: point.y };
      phase.value = 2;
      return;
    }
    commitPreview({
      ...preview.value,
      width: point.x - preview.value.x,
      height: point.y - preview.value.y,
    });
  }

  function commitPreview(shape) {
    cancelPlacement();
    // One entry for the whole gesture: a highlight and the caption it spawns
    // are one thought, so one undo should take both back.
    remember();
    const normalized =
      shape.kind === 'arrow'
        ? shape // an arrow's extent is a direction, so negatives are meaningful
        : {
            ...shape,
            x: Math.min(shape.x, shape.x + shape.width),
            y: Math.min(shape.y, shape.y + shape.height),
            width: Math.abs(shape.width),
            height: Math.abs(shape.height),
          };

    if (normalized.kind === 'crop') {
      applyCrop(normalized);
      redraw();
      return;
    }
    if (normalized.kind === 'text') {
      addTextBox({
        ...normalized,
        width: Math.max(normalized.width, region().width * 0.18),
        height: Math.max(normalized.height, textBoxHeight('', normalized.width, fontSize.value)),
      });
      return;
    }

    shot.value.shapes.push(normalized);
    selectedIndex.value = shot.value.shapes.length - 1;
    redraw();

    // Marking a thing and saying what is wrong with it are one thought, so a
    // highlight or an arrow drops straight into its caption. The shape itself
    // is excluded from the collision check — an arrow's bounding box spans
    // tail to head, so counting it would push every caption to the fallback.
    const others = shot.value.shapes.filter((shape) => shape !== normalized);
    if (normalized.kind === 'highlight') {
      addTextBox(placeTextBox(normalized, region(), others, fontSize.value), { auto: true });
    } else if (normalized.kind === 'arrow') {
      addTextBox(placeTextBoxForArrow(normalized, region(), others, fontSize.value), { auto: true });
    }
  }

  /**
   * `auto` marks a caption the tool spawned for you rather than one you asked
   * for. Those are one-shot: after it is finished the previous tool comes
   * back, so annotating three arrows is click-type-click-type, not
   * click-type-press-A-click-type.
   */
  function addTextBox(box, { auto = false } = {}) {
    if (auto) toolBeforeCaption = tool.value;
    shot.value.shapes.push({ kind: 'text', text: '', fontSize: fontSize.value, ...box });
    tool.value = 'text';
    startEditing(shot.value.shapes.length - 1);
  }

  // ── pointer ──────────────────────────────────────────────────────────

  function onPointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return;
    if (editingIndex.value >= 0) {
      commitText();
      return;
    }
    stage.value?.focus();
    event.target.setPointerCapture?.(event.pointerId);
    dragStart = toSource(event);
    dragMoved = false;
  }

  function onPointerMove(event) {
    const point = toSource(event);

    if (dragStart && phase.value === 0) {
      const moved =
        Math.abs(point.clientX - dragStart.clientX) > DRAG_THRESHOLD_PX ||
        Math.abs(point.clientY - dragStart.clientY) > DRAG_THRESHOLD_PX;
      if (moved) {
        dragMoved = true;
        preview.value = {
          kind: tool.value,
          x: dragStart.x,
          y: dragStart.y,
          width: point.x - dragStart.x,
          height: point.y - dragStart.y,
        };
      }
      return;
    }

    if (phase.value === 1) {
      // The whole shape rides along under the cursor.
      preview.value = { ...preview.value, x: point.x, y: point.y };
    } else if (phase.value === 2) {
      preview.value = {
        ...preview.value,
        width: point.x - preview.value.x,
        height: point.y - preview.value.y,
      };
    }
  }

  function onPointerUp(event) {
    const point = toSource(event);
    const wasDragging = dragMoved;
    dragStart = null;
    dragMoved = false;

    if (wasDragging && phase.value === 0) {
      const shape = preview.value;
      // A twitch is not an annotation.
      if (shape && (Math.abs(shape.width) > 6 || Math.abs(shape.height) > 6)) {
        commitPreview({ ...shape, width: point.x - shape.x, height: point.y - shape.y });
      } else {
        cancelPlacement();
      }
      return;
    }
    advancePlacement(point);
  }

  function applyCrop(shape) {
    const area = region();
    // Crops compose: a second crop is relative to what is on screen, and the
    // shapes already live in source coordinates, so only the region moves.
    shot.value.crop = {
      x: Math.max(area.x, shape.x),
      y: Math.max(area.y, shape.y),
      width: Math.min(shape.width, area.x + area.width - shape.x),
      height: Math.min(shape.height, area.y + area.height - shape.y),
    };
  }

  function resetCrop() {
    remember();
    shot.value.crop = null;
    redraw();
  }

  function undo() {
    // A half-placed shape is not yet history; the first undo abandons it.
    if (phase.value > 0) {
      cancelPlacement();
      return;
    }
    if (editingIndex.value >= 0) commitText();
    if (!past.value.length) return;
    future.value.push(snapshot());
    restore(past.value.pop());
  }

  function redo() {
    if (editingIndex.value >= 0) commitText();
    if (!future.value.length) return;
    past.value.push(snapshot());
    restore(future.value.pop());
  }

  /** Keyboard/touch path: drop a shape in the middle, then nudge it. */
  function addCentredShape() {
    const area = region();
    const extent = defaultExtent();
    const box = {
      x: area.x + (area.width - extent.width) / 2,
      y: area.y + (area.height - extent.height) / 2,
      ...extent,
    };
    cancelPlacement();
    commitPreview({ kind: tool.value === 'crop' ? 'redact' : tool.value, ...box });
    stage.value?.focus();
  }

  // ── keyboard ─────────────────────────────────────────────────────────

  function isTyping(event) {
    const target = event.target;
    return !!target && ['TEXTAREA', 'INPUT'].includes(target.tagName);
  }

  function onKeyDown(event) {
    if (isTyping(event)) return;

    if (event.key === 'Escape' && phase.value > 0) {
      // Consumed here so the surrounding dialog does not also close.
      event.preventDefault();
      event.stopImmediatePropagation();
      cancelPlacement();
      return;
    }
    if (event.metaKey || event.ctrlKey) {
      const key = event.key.toLowerCase();
      // Cmd/Ctrl+R would reload the page and lose the whole draft, so it is
      // taken over while the editor is open. Shift+Z redoes too, for the
      // people whose fingers already know that one.
      if (key === 'r' || (key === 'z' && event.shiftKey)) {
        event.preventDefault();
        redo();
        return;
      }
      if (key === 'z') {
        event.preventDefault();
        undo();
        return;
      }
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    const key = event.key.toLowerCase();
    if (TOOL_KEYS[key]) {
      event.preventDefault();
      selectTool(TOOL_KEYS[key]);
      return;
    }
    if (key === 'b') {
      event.preventDefault();
      addCentredShape();
      return;
    }

    const shape = shot.value?.shapes[selectedIndex.value];
    if (!shape) return;

    if (event.key === 'Enter' && shape.kind === 'text') {
      event.preventDefault();
      startEditing(selectedIndex.value);
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      remember();
      shot.value.shapes.splice(selectedIndex.value, 1);
      selectedIndex.value = shot.value.shapes.length - 1;
      redraw();
      return;
    }
    const nudge = Math.max(4, region().width * NUDGE_RATIO);
    const deltas = {
      ArrowLeft: [-nudge, 0],
      ArrowRight: [nudge, 0],
      ArrowUp: [0, -nudge],
      ArrowDown: [0, nudge],
    };
    const delta = deltas[event.key];
    if (!delta) return;
    event.preventDefault();
    remember();
    if (event.shiftKey) {
      shape.width = Math.max(8, shape.width + delta[0]);
      shape.height = Math.max(8, shape.height + delta[1]);
    } else {
      shape.x += delta[0];
      shape.y += delta[1];
    }
    redraw();
  }

  onMounted(() => {
    redraw();
    // On window, not the stage: the shortcuts should work the moment the
    // editor opens, without first clicking into the image.
    window.addEventListener('keydown', onKeyDown);
  });

  onBeforeUnmount(() => window.removeEventListener('keydown', onKeyDown));

  watch(() => props.shotId, redraw);
</script>
